/* =============================================================================
 *  Kreux Matrix — FAQ Glassmorphic Accordion (UI shell v2)
 *  Sits between PerformanceLayer and TerminalLayer inside the #0A1520 carrier.
 *  Bilingual EN/DE via useLang() · single-open · grid-rows smooth expand.
 *  Background: hex matrix + subtle data nodes matching the previous section.
 * ===========================================================================*/

import { useState } from "react";
import { useLang } from "@/lib/LangContext";

const dict = {
  en: {
    Header: "Absolute Transparency. Zero Variables.",
    Subtext: "Clear parameters for enterprise-grade deployment.",
    Items: [
      {
        question: "Will our proprietary company data be used to train public AI models?",
        answer: "Absolutely not. We exclusively utilize Enterprise-grade API endpoints. Under strict enterprise agreements, your data is never retained, shared, or used to train external models. Our entire architecture is built for 100% GDPR compliance, ensuring your trade secrets and client data remain strictly within your control."
      },
      {
        question: "Do we need to overhaul our existing software to integrate your AI solutions?",
        answer: "No. Kreux Matrix builds modular automation systems designed to integrate seamlessly into your existing tech stack (CRMs, ERPs, internal databases, and communication channels). We act as an invisible, highly efficient layer on top of your current operations, meaning zero downtime and no steep learning curves for your team."
      },
      {
        question: "How is Kreux Matrix different from standard software agencies or AI consultants?",
        answer: "We do not sell theoretical advice or off-the-shelf software subscriptions. We are your collaborative AI engineering partner. We architect custom automation engines designed to solve specific, high-cost bottlenecks in your workflow. Our singular focus is on measurable ROI: reducing manual labor hours, eliminating human error, and radically accelerating your operational output."
      },
      {
        question: "What happens after the automation system is deployed?",
        answer: "A true B2B partnership does not end at deployment. As your dedicated AI partner, we provide continuous monitoring, maintenance, and optimization for the systems we build. As AI models evolve and your business scales, we proactively adapt your architecture to ensure peak performance. You maintain a permanent competitive edge without needing to hire an expensive in-house AI engineering team."
      },
      {
        question: "How do you structure pricing, and how long does a typical implementation take?",
        answer: "We do not believe in open-ended, hourly billing that drains your budget. Every Kreux Matrix architecture is scoped as a fixed-investment project based on the specific ROI it will generate for your business. Because our frameworks are modular, a typical deployment—from the initial audit to the live system—takes weeks, not months. You get a clear timeline, a transparent price, and a rapid return on investment."
      }
    ]
  },
  de: {
    Header: "Absolute Transparenz. Keine Variablen.",
    Subtext: "Klare Parameter für den Enterprise-Einsatz.",
    Items: [
      {
        question: "Werden unsere sensiblen Unternehmensdaten für das Training öffentlicher KI-Modelle verwendet?",
        answer: "Absolut nicht. Wir nutzen ausschließlich Enterprise-API-Endpunkte. Gemäß strengen Enterprise-Verträgen werden Ihre Daten niemals gespeichert, geteilt oder für das Training externer Modelle verwendet. Unsere gesamte Architektur ist auf 100%ige DSGVO-Konformität ausgelegt, sodass Ihre Geschäftsgeheimnisse und Kundendaten strikt unter Ihrer Kontrolle bleiben."
      },
      {
        question: "Müssen wir unsere bestehende Software umstrukturieren, um Ihre KI-Lösungen zu integrieren?",
        answer: "Nein. Kreux Matrix entwickelt modulare Automatisierungssysteme, die sich nahtlos in Ihre bestehende Tech-Infrastruktur (CRMs, ERPs, interne Datenbanken und Kommunikationskanäle) integrieren lassen. Wir fungieren als unsichtbare, hocheffiziente Schicht über Ihren aktuellen Abläufen – das bedeutet null Ausfallzeiten und keine steilen Lernkurven für Ihr Team."
      },
      {
        question: "Wie unterscheidet sich Kreux Matrix von klassischen Softwareagenturen oder KI-Beratern?",
        answer: "Wir verkaufen keine theoretische Beratung oder Standard-Softwareabonnements. Wir sind Ihr kollaborativer Partner für KI-Entwicklung. Wir konstruieren maßgeschneiderte Automatisierungsmaschinen, die spezifische, kostenintensive Engpässe in Ihrem Workflow lösen. Unser einziger Fokus liegt auf messbarem ROI: Reduzierung manueller Arbeitsstunden, Eliminierung menschlicher Fehler und radikale Beschleunigung Ihrer operativen Leistung."
      },
      {
        question: "Was passiert, nachdem das Automatisierungssystem implementiert wurde?",
        answer: "Eine echte B2B-Partnerschaft endet nicht mit der Implementierung. Als Ihr engagierter KI-Partner bieten wir kontinuierliche Überwachung, Wartung und Optimierung für die von uns entwickelten Systeme. Wenn sich KI-Modelle weiterentwickeln und Ihr Unternehmen wächst, passen wir Ihre Architektur proaktiv an, um maximale Leistung zu gewährleisten. Sie behalten einen dauerhaften Wettbewerbsvorteil, ohne ein teures internes KI-Entwicklerteam einstellen zu müssen."
      },
      {
        question: "Wie gestalten sich Ihre Preise und wie lange dauert eine typische Implementierung?",
        answer: "Wir glauben nicht an unbegrenzte, stundenbasierte Abrechnungen, die Ihr Budget belasten. Jede Kreux Matrix-Architektur wird als Festpreisprojekt kalkuliert, basierend auf dem spezifischen ROI, den sie für Ihr Unternehmen generiert. Da unsere Frameworks modular aufgebaut sind, dauert eine typische Implementierung – vom ersten Audit bis zum Live-System – Wochen, nicht Monate. Sie erhalten einen klaren Zeitplan, einen transparenten Preis und einen schnellen Return on Investment."
      }
    ]
  }
};

/* ─── Background: Hex matrix (mirrors PerformanceLayer's kx-hex-zh) ───── */
const HexMatrix = () => (
  <svg
    aria-hidden="true"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute inset-0"
  >
    <defs>
      <pattern id="kx-hex-faq" x="0" y="0" width="56" height="98" patternUnits="userSpaceOnUse">
        <path
          d="M28 0 L56 16 L56 48 L28 64 L0 48 L0 16 Z M0 48 L0 80 L28 96 L56 80 L56 48"
          fill="none"
          stroke="rgba(255,255,255,0.03)"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#kx-hex-faq)" />
  </svg>
);

/* ─── Background: subtle drifting data nodes ──────────────────────────── */
const DataNodes = () => {
  const nodes = [
    { top: "12%", left: "8%",  delay: "0s"   },
    { top: "22%", left: "88%", delay: "1.4s" },
    { top: "48%", left: "4%",  delay: "2.8s" },
    { top: "62%", left: "94%", delay: "0.7s" },
    { top: "80%", left: "12%", delay: "2.1s" },
    { top: "36%", left: "72%", delay: "3.5s" },
    { top: "88%", left: "78%", delay: "1.0s" }
  ];
  return (
    <div aria-hidden="true" className="absolute inset-0">
      {nodes.map((n, i) => (
        <span
          key={i}
          className="faq-node absolute w-1 h-1 rounded-full bg-[var(--il-cyan)]/40"
          style={{
            top: n.top,
            left: n.left,
            animationDelay: n.delay,
            boxShadow: "0 0 8px rgba(0,229,255,0.5)"
          }}
        />
      ))}
    </div>
  );
};

/* ─── Expand icon: cyan + rotates 45° → × on open, glow pulses when active ─ */
const PlusIcon = ({ open }) => (
  <span
    aria-hidden="true"
    data-icon-state={open ? "open" : "closed"}
    className={
      "faq-icon shrink-0 inline-flex items-center justify-center w-6 h-6 text-[var(--il-cyan)] transition-transform duration-500 ease-out " +
      (open ? "rotate-45" : "rotate-0")
    }
  >
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <line x1="12" y1="5"  x2="12" y2="19" />
      <line x1="5"  y1="12" x2="19" y2="12" />
    </svg>
  </span>
);

const FAQ = () => {
  const { lang } = useLang();
  const t = dict[lang] || dict.en;
  const faqData = t.Items;
  const [openIdx, setOpenIdx] = useState(-1);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative w-full font-sans overflow-hidden"
      style={{
        "--il-cyan": "#00E5FF",
        background: "#0A1520",
        color: "#FFFFFF"
      }}
    >
      <style>{styles}</style>

      {/* Absolute background — matches previous section's hex grid + data nodes */}
      <div aria-hidden="true" className="absolute inset-0 z-0 pointer-events-none opacity-70">
        <HexMatrix />
        <DataNodes />
      </div>

      {/* Content wrapper */}
      <div className="relative z-10 max-w-4xl mx-auto py-32 px-6">
        <h2
          data-testid="faq-header"
          className="text-white font-bold text-4xl mb-2 text-center tracking-tight"
        >
          {t.Header}
        </h2>
        <p
          data-testid="faq-subtext"
          className="text-gray-400 text-lg mb-12 text-center"
        >
          {t.Subtext}
        </p>

        {/* Accordion */}
        <div data-testid="faq-list">
          {faqData.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div
                key={i}
                data-testid={`faq-item-${i}`}
                data-open={isOpen ? "true" : "false"}
                onClick={() => setOpenIdx(isOpen ? -1 : i)}
                role="button"
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${i}`}
                className={
                  "faq-item bg-white/5 backdrop-blur-3xl border border-white/10 rounded-xl mb-4 p-6 cursor-pointer transition-all duration-500 ease-out " +
                  "hover:border-[var(--il-cyan)]/60 hover:shadow-[0_0_30px_rgba(0,255,255,0.15)] hover:bg-white/10 " +
                  (isOpen ? "border-[var(--il-cyan)] shadow-[0_0_40px_rgba(0,255,255,0.25)] bg-white/10 is-active" : "")
                }
              >
                <div
                  className="text-white font-semibold text-lg flex justify-between items-center gap-6"
                  data-testid={`faq-question-${i}`}
                >
                  <span className="text-left">{item.question}</span>
                  <PlusIcon open={isOpen} />
                </div>

                {/* Answer — grid-rows trick for buttery smooth height animation */}
                <div
                  id={`faq-answer-${i}`}
                  data-testid={`faq-answer-${i}`}
                  className="grid transition-all duration-500 ease-in-out overflow-hidden"
                  style={{
                    gridTemplateRows: isOpen ? "1fr" : "0fr",
                    opacity: isOpen ? 1 : 0
                  }}
                  aria-hidden={!isOpen}
                >
                  <div className="overflow-hidden">
                    <p className="text-gray-300 mt-4 leading-relaxed m-0">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const styles = `
/* Idle icon → cyan; hover state on the item softly glows the icon;
   active/open state pulses the icon with heavy cyan drop-shadow. */
.faq-item:hover .faq-icon {
  filter: drop-shadow(0 0 5px rgba(0, 255, 255, 0.8));
}
.faq-item.is-active .faq-icon {
  animation: faqIconPulse 1.6s ease-in-out infinite;
}
@keyframes faqIconPulse {
  0%, 100% { filter: drop-shadow(0 0 15px rgba(0, 255, 255, 1)); }
  50%      { filter: drop-shadow(0 0 6px  rgba(0, 255, 255, 0.6)); }
}

/* Drifting data nodes — subtle breathing */
@keyframes faqNodeDrift {
  0%, 100% { opacity: 0.25; transform: translateY(0) scale(1); }
  50%      { opacity: 0.75; transform: translateY(-4px) scale(1.4); }
}
.faq-node { animation: faqNodeDrift 5s ease-in-out infinite; will-change: opacity, transform; }

@media (prefers-reduced-motion: reduce) {
  .faq-item, .faq-icon, .faq-node { animation: none !important; transition: none !important; }
}
`;

export default FAQ;
